// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import styles from './LoopPicker.module.css';
import { RankedLoop, loopComponentCount } from './loop-ranking';

interface LoopPickerProps {
  /** Loops in ranked order (the Editor passes the output of rankLoops). */
  loops: readonly RankedLoop[];
  /** The loops currently highlighted on the canvas -- CO-5 pins several at once,
   *  plus a transient hover preview, so this is a set rather than one id. */
  activeLoopIds?: ReadonlySet<string>;
  /** Per-loop badge fill (CO-5, Williams 2026-08-07), keyed by loop id, so a
   *  row's badge matches its ring/badge on the canvas. Absent -> polarity CSS. */
  loopColors?: ReadonlyMap<string, string>;
  /** Hover preview -- same signature as the CLD badge's onHoverChange, so the
   *  picker and the canvas drive the Editor's highlight through one handler. */
  onHover?: (id: string, hovering: boolean) => void;
  /** Click to pin/unpin the loop's highlight. */
  onSelect?: (id: string) => void;
}

const POLARITY_WORD: Record<RankedLoop['polarity'], string> = {
  reinforcing: 'Reinforcing',
  balancing: 'Balancing',
  unknown: 'Undetermined',
};

/**
 * The Loop Picker (MVP WS2, CO-4 option (a)): a ranked list of the model's
 * feedback loops in the Loops view. Rows are ordered by the engine's importance
 * ranking (by LTM score once a WS3 analyze run supplies one), and each row
 * hovers/pins the loop's highlight on the canvas through the same AC2.2 seam the
 * CLD badges use. Loop Picker option (b) ("combine weak loops") is post-MVP.
 *
 * Presentation-only: the Editor owns the ranked list and the active loop, and
 * hover/select flow back out through callbacks.
 */
export class LoopPicker extends React.PureComponent<LoopPickerProps> {
  render(): React.ReactNode {
    const { loops, activeLoopIds, loopColors, onHover, onSelect } = this.props;

    if (loops.length === 0) {
      return (
        <div className={styles.picker}>
          <div className={styles.header}>Loops</div>
          <div className={styles.empty}>No feedback loops detected in this model.</div>
        </div>
      );
    }

    // Warn when the loops split into disconnected systems, so a single-canvas
    // render isn't mistaken for one connected model (Williams 2026-08-07).
    const components = loopComponentCount(loops);

    return (
      <div className={styles.picker}>
        <div className={styles.header}>
          Loops <span className={styles.sub}>· by dominance</span>
        </div>
        {components > 1 ? (
          <div className={styles.warning} role="status">
            This model has {components} disconnected loop systems — they don&apos;t share any
            variables.
          </div>
        ) : null}
        <ul className={styles.list} role="listbox" aria-label="Feedback loops">
          {loops.map((loop) => {
            const active = activeLoopIds?.has(loop.id) ?? false;
            const rowClass = active ? `${styles.row} ${styles.rowActive}` : styles.row;
            return (
              <li key={loop.id}>
                <button
                  type="button"
                  className={rowClass}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={onHover ? () => onHover(loop.id, true) : undefined}
                  onMouseLeave={onHover ? () => onHover(loop.id, false) : undefined}
                  onClick={onSelect ? () => onSelect(loop.id) : undefined}
                >
                  <span
                    className={`${styles.badge} ${styles[`badge_${loop.polarity}`]}`}
                    style={loopColors?.get(loop.id) !== undefined ? { backgroundColor: loopColors.get(loop.id) } : undefined}
                  >
                    {loop.label}
                  </span>
                  <span className={styles.meta}>
                    <span className={styles.polarity}>{POLARITY_WORD[loop.polarity]}</span>
                    <span className={styles.vars}>{loop.variables.join(' → ')}</span>
                  </span>
                  {loop.score !== undefined ? (
                    <span className={styles.score}>{Math.round(loop.score * 100)}%</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
}
